import React, {ReactNode, useCallback, useMemo} from 'react';

import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {refreshDetails} from 'insights/analytics-dashboards/details/AnalyticsDashboardDetailsActions';
import {useAnalyticsDashboardContext} from 'insights/analytics-dashboards/details/AnalyticsDashboardDetailsContext';
import {GetAnalyticsDashboard} from 'insights/analytics-dashboards/details/graphql/__generated/GetAnalyticsDashboard';
import {
  analyticsDashboardGlobalScreenState,
  BlockEditorPane,
  BlockEditorScreenState,
  collapsePane,
  expandPane,
  isBlockEditorScreenState,
} from 'insights/analytics-dashboards/details/view-state/ScreenState';
import {AnalyticsQuerySpecRequest} from 'insights/analytics-queries/RESTAnalyticsQueryTypes';
import {
  exportAnalyticsQueryOutputToCSV,
  runGraphQLAnalyticsQuery,
} from 'insights/analytics-queries/run-query/RunAnalyticsQueryActions';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import {updateAnalyticsVisualization} from 'insights/analytics-visualizations/AnalyticsVisualizationActions';
import {INSIGHTS_HELP_URL, SQL_TEMPLATE_FORM_HEIGHT} from 'insights/InsightsConstants';
import {AnalyticsParameterValue} from 'insights/parameters/__generated/AnalyticsParameterValue';
import getAddParameterMenuOption from 'insights/parameters/getAddParameterMenuOption';
import {AnalyticsParameterConfigSpecRequest} from 'insights/parameters/RESTParameterTypes';
import {ExportType, RunQueryState} from 'insights/warehouse/AnalyticsWarehouseTypes';
import SQLEditorPane from 'insights/warehouse/sql-editor/SQLEditorPane';
import WarehouseTableBrowser, {
  useWarehouseTableBrowserState,
} from 'insights/warehouse/table-browser/WarehouseTableBrowser';
import DynamicLayout, {
  DynamicLayoutColumn,
  DynamicLayoutRow,
  OneColumnDynamicLayoutRow,
} from 'ui-library/DynamicLayout';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import legacyRunModal from 'ui-library/legacyRunModal';
import OverflowMenu, {DIVIDER} from 'ui-library/menus/OverflowMenu';

import {AnalyticsParameterConfigGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';
import SQLAssistantButton from '../ai-assistant/SQLAssistantButton';
import AnalyticsBlockEditorPaneToggleExpandButton from './AnalyticsBlockEditorPaneToggleExpandButton';
import AnalyticsQueryOutputPaneGroup from './AnalyticsQueryOutputPaneGroup';
import tryFormatSQLQuery from './formatSQL';
import {BlockEditorUIState} from './reduceBlockEditorUIs';
import SaveUserDefinedViewModal from './SaveUserDefinedViewModal';
import EditSQLButton from './sql-template-form/EditSQLButton';
import SQLTemplateForm from './sql-template-form/SQLTemplateForm';
import {SQLTemplateFormState} from './sql-template-form/SQLTemplateFormTypes';

type AnalyticsDashboard = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>;

type AnalyticsQuery = AnalyticsDashboard['analyticsQueries'][number];

interface UnconnectedActions {
  setDraftSQLQuery: (newDraftQuery: string) => void;
  setSQLSubQuery: (newSubQuery: string | null) => void;
  setSQLTemplateFormState: (newSQLTemplateFormState: SQLTemplateFormState) => void;
  saveAndRunQuery: (newSQLQuery?: string) => Promise<void>;
  switchToSQLEditor: () => void;
  updateAnalyticsQuery: (
    analyticsQueryId: string,
    data: AnalyticsQuerySpecRequest,
    additionalParameterConfigs?: Array<AnalyticsParameterConfigSpecRequest>
  ) => Promise<void>;
}
interface ConnectedActions {
  collapsePane: () => void;
  expandPane: (pane: BlockEditorPane) => void;
  exportAnalyticsQueryOutputToCSV: typeof exportAnalyticsQueryOutputToCSV;
  runGraphQLAnalyticsQuery: typeof runGraphQLAnalyticsQuery;
  updateAnalyticsVisualization: typeof updateAnalyticsVisualization;
}
export type Actions = UnconnectedActions & ConnectedActions;

export interface AnalyticsBlockEditorContentProps {
  actions: Actions;
  // For ExtraDataToTrackRunQuery
  analyticsDashboardAttrs: Pick<AnalyticsDashboard, 'id' | 'createdAt' | 'creator' | 'modifiedAt' | 'name'>;
  analyticsQuery: AnalyticsQuery;
  blockEditorUIState: BlockEditorUIState;
  draftSQLQuery: string;
  isDisplayingSubQueryResults: boolean;
  parameterConfigs: Array<AnalyticsParameterConfigGraphQL>;
  parameterList: ReactNode;
  parameterValues: Array<AnalyticsParameterValue>;
  runQueryState: RunQueryState;
  screenState: BlockEditorScreenState;
}

export function AnalyticsBlockEditorContent(props: AnalyticsBlockEditorContentProps): JSX.Element {
  const {
    screenState,
    actions: {collapsePane, expandPane},
  } = props;
  const isRunning = props.runQueryState.status === AsyncStatus.LOADING;
  const blockEditorContentTestAttrs = {
    component: 'AnalyticsBlockEditorContent',
    element: 'root',
  } as const;

  function handleExportToCSV(exportType: ExportType): void {
    props.actions.exportAnalyticsQueryOutputToCSV(
      props.analyticsQuery,
      props.parameterConfigs,
      props.parameterValues,
      {dashboardName: props.analyticsDashboardAttrs.name, exportType}
    );
  }

  const handleToggleExpandPane = useCallback(
    (pane: BlockEditorPane, newIsExpanded: boolean) => {
      if (newIsExpanded) {
        expandPane(pane);
      } else {
        collapsePane();
      }
    },
    [collapsePane, expandPane]
  );

  function handleAddParameter(newParameterName: string): void {
    const newSQLQuery = `${props.draftSQLQuery}\n{{${newParameterName}}}`;
    props.actions.setDraftSQLQuery(newSQLQuery);
    props.actions.saveAndRunQuery(newSQLQuery);
  }

  function formatSQLQuery(): void {
    const newSQLQuery = tryFormatSQLQuery(props.draftSQLQuery);

    if (newSQLQuery != null && newSQLQuery !== props.draftSQLQuery) {
      props.actions.setDraftSQLQuery(newSQLQuery);
    }
  }

  const sqlEditorPane = (
    <SQLEditorPane
      additionalActionButtons={
        <>
          {BenchlingConfig.INSIGHTS_ENABLE_AI_SQL_ASSISTANT && (
            <SQLAssistantButton
              draftSQLQuery={props.draftSQLQuery}
              parameterConfigs={props.parameterConfigs}
              parameterValues={props.parameterValues}
              setDraftSQLQuery={props.actions.setDraftSQLQuery}
              saveAndRunQuery={props.actions.saveAndRunQuery}
            />
          )}
          <OverflowMenu
            enableTooltip={true}
            buttonProps={{
              dataTestAttrs: {
                component: 'AnalyticsBlockEditorContent',
                element: 'sqlEditorOverflowButton',
              },
            }}
            menuProps={{dropLeft: true}}
            options={[
              getAddParameterMenuOption({
                sqlQuery: props.draftSQLQuery,
                onAddParameter: handleAddParameter,
                disabled: isRunning,
              }),
              {
                icon: 'note-code',
                contents: <span>Format SQL</span>,
                onSelect: formatSQLQuery,
              },
              {
                icon: 'save' as const,
                contents: <span>Save as a custom view</span>,
                onSelect: () =>
                  legacyRunModal(SaveUserDefinedViewModal, {
                    sqlQuery: props.draftSQLQuery,
                    onRequestClose: () => {},
                    onRequestSubmit: () => {},
                    originAnalyticsQueryApiIdentifier: props.analyticsQuery.id,
                    parameterConfigs: props.parameterConfigs,
                    parameterValues: props.parameterValues,
                  }),
              },
              DIVIDER,
              {
                icon: 'open',
                contents: <span>Help with dashboards</span>,
                href: INSIGHTS_HELP_URL,
                openInNewTab: true,
              },
            ]}
          />
          <AnalyticsBlockEditorPaneToggleExpandButton
            className="u-mini-spacing-left"
            dataTestAttrs={{
              component: 'AnalyticsBlockEditorContent',
              element: 'sqlEditorToggleExpandButton',
            }}
            expandedPane={screenState.expandedPane}
            onToggleExpandPane={handleToggleExpandPane}
            pane={BlockEditorPane.SQL_EDITOR}
          />
        </>
      }
      actions={{
        saveAndRunQuery: props.actions.saveAndRunQuery,
        setSQLQuery: props.actions.setDraftSQLQuery,
        setSQLSubQuery: props.actions.setSQLSubQuery,
      }}
      isExpanded={screenState.expandedPane === BlockEditorPane.SQL_EDITOR}
      isRunning={isRunning}
      sqlQuery={props.draftSQLQuery}
    />
  );

  // Without the useMemo call, the toggle button made the WarehouseTableBrowser's useMemo call ineffective
  // because the buttons were a dependency that would cause it to re-render every render. This is problematic
  // because WarehouseTableBrowser is expensive to render (there can be a lot of tables to list), and we
  // render frequently when the user is typing in the sql editor (the result was lag when typing).
  const warehouseTableBrowserButtons = useMemo(
    () => (
      <AnalyticsBlockEditorPaneToggleExpandButton
        dataTestAttrs={{
          component: 'AnalyticsBlockEditorContent',
          element: 'warehouseBrowserToggleExpandButton',
        }}
        expandedPane={screenState.expandedPane}
        onToggleExpandPane={handleToggleExpandPane}
        pane={BlockEditorPane.WAREHOUSE_BROWSER}
      />
    ),
    [screenState.expandedPane, handleToggleExpandPane]
  );

  const warehouseBrowserState = useWarehouseTableBrowserState();

  const warehouseTableBrowser = (
    <WarehouseTableBrowser
      additionalActionButtons={warehouseTableBrowserButtons}
      warehouseBrowserState={warehouseBrowserState}
    />
  );

  let queryEditor;
  const sqlTemplateFormState = props.blockEditorUIState.sqlTemplateFormState;

  if (sqlTemplateFormState != null) {
    queryEditor = (
      <OneColumnDynamicLayoutRow height={SQL_TEMPLATE_FORM_HEIGHT}>
        <SQLTemplateForm
          {...props}
          formType={sqlTemplateFormState.formType}
          additionalActionButtons={
            <EditSQLButton
              editSQL={props.actions.switchToSQLEditor}
              sqlTemplateFormState={sqlTemplateFormState}
            />
          }
          sqlTemplateFormState={sqlTemplateFormState}
        />
      </OneColumnDynamicLayoutRow>
    );
  } else {
    queryEditor = (
      <DynamicLayoutRow>
        <DynamicLayoutColumn widthProportion={7 / 12}>{sqlEditorPane}</DynamicLayoutColumn>
        <DynamicLayoutColumn widthProportion={5 / 12}>{warehouseTableBrowser}</DynamicLayoutColumn>
      </DynamicLayoutRow>
    );
  }

  const queryOutputPaneRows = (
    <AnalyticsQueryOutputPaneGroup
      {...props}
      actions={{
        exportBlock: handleExportToCSV,
        toggleExpandPane: handleToggleExpandPane,
        updateAnalyticsVisualization: props.actions.updateAnalyticsVisualization,
      }}
      isExpanded={screenState.expandedPane === BlockEditorPane.VISUALIZATION_EDITOR}
      isRunning={isRunning}
    />
  );

  switch (screenState.expandedPane) {
    case BlockEditorPane.SQL_EDITOR:
      return (
        <DynamicLayout dataTestAttrs={blockEditorContentTestAttrs}>
          <OneColumnDynamicLayoutRow>{sqlEditorPane}</OneColumnDynamicLayoutRow>
          {queryOutputPaneRows}
        </DynamicLayout>
      );

    case BlockEditorPane.VISUALIZATION_EDITOR:
      return <DynamicLayout dataTestAttrs={blockEditorContentTestAttrs}>{queryOutputPaneRows}</DynamicLayout>;

    case BlockEditorPane.WAREHOUSE_BROWSER:
      return (
        <DynamicLayout dataTestAttrs={blockEditorContentTestAttrs}>
          <OneColumnDynamicLayoutRow>{warehouseTableBrowser}</OneColumnDynamicLayoutRow>
        </DynamicLayout>
      );
    default:
      assertAndContinue(screenState.expandedPane == null, 'Unsupported expanded pane for block editor', {
        screenState,
      });
      return (
        <DynamicLayout dataTestAttrs={blockEditorContentTestAttrs}>
          {queryEditor}
          {queryOutputPaneRows}
        </DynamicLayout>
      );
  }
}

export default function ConnectedAnalyticsBlockEditorContent(
  props: Omit<AnalyticsBlockEditorContentProps, 'actions' | 'screenState'> & {actions: UnconnectedActions}
): JSX.Element | null {
  const {analyticsDashboardViewStateId} = useAnalyticsDashboardContext();
  const [screenState, setScreenState] = analyticsDashboardGlobalScreenState.useGlobalClientState(
    analyticsDashboardViewStateId
  );

  if (!isBlockEditorScreenState(screenState)) {
    return null;
  }

  async function handleUpdateAnalyticsVisualization(
    initialAnalyticsVisualization: AnalyticsVisualization,
    newAnalyticsVisualization: AnalyticsVisualization
  ): Promise<void> {
    await updateAnalyticsVisualization(initialAnalyticsVisualization, newAnalyticsVisualization);
    await refreshDetails(props.analyticsDashboardAttrs.id);
  }

  return (
    <AnalyticsBlockEditorContent
      {...props}
      actions={{
        ...props.actions,
        collapsePane: () => {
          setScreenState(collapsePane(screenState));
        },
        expandPane: (pane: BlockEditorPane) => {
          setScreenState(expandPane(screenState, pane));
        },
        exportAnalyticsQueryOutputToCSV,
        runGraphQLAnalyticsQuery,
        updateAnalyticsVisualization: handleUpdateAnalyticsVisualization,
      }}
      screenState={screenState}
    />
  );
}
